import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { vehicleApi } from '../../../api/vehicleApi';
import { userApi } from '../../../api/userApi';
import VehicleModal from './VehicleModal';
import VehicleDetailModal from './VehicleDetailModal';
import VehicleDocumentModal from './VehicleDocumentModal';
import CategoryManagerModal from './CategoryManagerModal';
import VehicleTimeline from './VehicleTimeline';
import './VehicleStatus.css';

export default function VehicleStatus() {
    const location = useLocation();
    const navigate = useNavigate();
    const fromLeader = location.state?.fromLeader || false;

    const [vehicles, setVehicles] = useState([]);
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // UI State
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategoryId, setFilterCategoryId] = useState('all');
    
    // Modals
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        
        // Parallel but individual error handling
        const fetchVehicles = async () => {
            try {
                const data = await vehicleApi.getVehicles();
                setVehicles(data || []);
            } catch (err) {
                console.error('Error fetching vehicles:', err);
            }
        };

        const fetchUsers = async () => {
            try {
                const data = await userApi.getAllUsers();
                console.log('Fetched users list:', data);
                setUsers(data || []);
            } catch (err) {
                console.error('Error fetching users:', err);
                setUsers([]); // fallback to empty
            }
        };

        const fetchCategories = async () => {
            try {
                const data = await vehicleApi.getCategories();
                setCategories(data || []);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };

        await Promise.allSettled([
            fetchVehicles(),
            fetchUsers(),
            fetchCategories()
        ]);
        
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle Deep Linking from Leader Tool
    useEffect(() => {
        if (!loading && vehicles.length > 0 && location.state?.openVehicleId) {
            const vehicleToOpen = vehicles.find(v => String(v.id) === String(location.state.openVehicleId));
            if (vehicleToOpen) {
                setSelectedVehicle(vehicleToOpen);
                setIsDetailModalOpen(true);
                // Clear state to avoid reopening on refresh
                window.history.replaceState({ ...location.state, openVehicleId: null }, '');
            }
        }
    }, [loading, vehicles, location.state]);

    const handleOpenDocModal = (v, e) => {
        if (e) e.stopPropagation();
        setSelectedVehicle(v);
        setIsDocModalOpen(true);
    };

    const handleSaveVehicle = async (formData) => {
        try {
            await vehicleApi.createVehicle(formData); // Assuming formData is the payload
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            alert('Fehler beim Speichern: ' + (err.response?.data?.error || 'Unbekannter Fehler'));
        }
    };

    const handleUpdateVehicle = async (payload) => {
        try {
            await vehicleApi.updateVehicle(payload.id, payload);
            setIsModalOpen(false);
            setIsDetailModalOpen(false);
            fetchData();
        } catch (err) {
            alert('Fehler beim Aktualisieren');
        }
    };

    const handleDelete = async (id, e) => {
        if (e) e.stopPropagation();
        if (!window.confirm('Fahrzeug wirklich löschen?')) return;
        try {
            await vehicleApi.deleteVehicle(id);
            fetchData();
        } catch (err) {
            alert('Fehler beim Löschen');
        }
    };

    const handleOpenEdit = (v, e) => {
        if (e) e.stopPropagation();
        setSelectedVehicle(v);
        setIsModalOpen(true);
    };

    const openDetail = (v) => {
        setSelectedVehicle(v);
        setIsDetailModalOpen(true);
    };

    // Derived Stats
    const stats = {
        total: vehicles.length,
        ready: vehicles.filter(v => v.is_ready).length,
        defects: vehicles.filter(v => v.status === 'red').length,
        urgentTuv: vehicles.filter(v => {
            if (!v.next_tuv) return false;
            const diff = (new Date(v.next_tuv) - new Date()) / (1000 * 60 * 60 * 24);
            return diff < 30;
        }).length
    };

    // Filter Logic
    const filteredVehicles = vehicles.filter(v => {
        const matchesSearch = 
            v.internal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.make?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === 'all' || v.status === filterStatus;
        const matchesCategory = filterCategoryId === 'all' || v.category_id?.toString() === filterCategoryId;
        
        return matchesSearch && matchesStatus && matchesCategory;
    });

    const openStatPopup = (type) => {
        if (type === 'total') setPopupData({ title: 'Gesamt-Flotte', vehicles: vehicles, color: '#3b82f6' });
        if (type === 'ready') setPopupData({ title: 'Einsatzbereit', vehicles: vehicles.filter(v => v.is_ready), color: '#059669' });
        if (type === 'defects') setPopupData({ title: 'Defekt / Wartung', vehicles: vehicles.filter(v => v.status === 'red'), color: '#dc2626' });
        if (type === 'urgent') setPopupData({ title: 'Dringende Termine (TÜV < 30T)', vehicles: vehicles.filter(v => {
            if (!v.next_tuv) return false;
            const diff = (new Date(v.next_tuv) - new Date()) / (1000 * 60 * 60 * 24);
            return diff < 30;
        }), color: '#d97706' });
    };

    const getDeadlineStatus = (dateStr) => {
        if (!dateStr) return { label: 'Kein Datum', cls: 'ok' };
        const d = new Date(dateStr);
        const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
        const label = d.toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' });
        
        if (diff < 0) return { label, cls: 'urgent' };
        if (diff < 60) return { label, cls: 'warning' };
        return { label, cls: 'ok' };
    };

    return (
        <div className="vehicle-status-container">
            <div className="vehicle-status-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {fromLeader && (
                        <button 
                            className="btn-back-leader" 
                            onClick={() => {
                                const returnState = { from: '/tools/vehicleStatus' };
                                if (location.state?.conflictState) {
                                    returnState.conflictState = location.state.conflictState;
                                    if (location.state.fromConflictCenter) {
                                        returnState.restoreConflict = true;
                                    }
                                }
                                navigate('/tools/leader', { state: returnState });
                            }}
                        >
                            ← Zurück
                        </button>
                    )}
                    {/* Title removed in favor of global header */}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn-secondary" onClick={() => setIsCategoryModalOpen(true)}>
                        ⚙️ Fahrzeugtypen verwalten
                    </button>
                    <button className="btn-primary" onClick={() => { setSelectedVehicle(null); setIsModalOpen(true); }}>
                        + Fahrzeug hinzufügen
                    </button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="status-stats">
                <div className="stat-card" onClick={() => openStatPopup('total')} style={{ cursor: 'pointer' }}>
                    <div className="stat-icon">🚛</div>
                    <div className="stat-info">
                        <h3>Gesamt-Flotte</h3>
                        <div className="stat-value">{stats.total}</div>
                    </div>
                </div>
                <div className="stat-card" onClick={() => openStatPopup('ready')} style={{ cursor: 'pointer' }}>
                    <div className="stat-icon" style={{ background: '#ecfdf5' }}>✅</div>
                    <div className="stat-info">
                        <h3>Einsatzbereit</h3>
                        <div className="stat-value" style={{ color: '#059669' }}>{stats.ready}</div>
                    </div>
                </div>
                <div className="stat-card" onClick={() => openStatPopup('defects')} style={{ cursor: 'pointer' }}>
                    <div className="stat-icon" style={{ background: '#fef2f2' }}>🛠️</div>
                    <div className="stat-info">
                        <h3>Defekt / Wartung</h3>
                        <div className="stat-value" style={{ color: '#dc2626' }}>{stats.defects}</div>
                    </div>
                </div>
                <div className="stat-card" onClick={() => openStatPopup('urgent')} style={{ cursor: 'pointer' }}>
                    <div className="stat-icon" style={{ background: '#fffbeb' }}>⚠️</div>
                    <div className="stat-info">
                        <h3>Dringende Termine</h3>
                        <div className="stat-value" style={{ color: '#d97706' }}>{stats.urgentTuv}</div>
                    </div>
                </div>
            </div>

            {/* Toolbar / Filters */}
            <div className="vehicle-toolbar">
                <input 
                    type="text" 
                    className="search-input" 
                    placeholder="🔍 Suche nach Name, Kennzeichen oder Marke..." 
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
                <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="all">Alle Status</option>
                    <option value="green">Einsatzbereit</option>
                    <option value="yellow">Eingeschränkt</option>
                    <option value="red">Defekt</option>
                </select>
                <select className="filter-select" value={filterCategoryId} onChange={e => setFilterCategoryId(e.target.value)}>
                    <option value="all">Alle Kategorien</option>
                    {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>

            {/* Main List */}
            <div className="vehicle-list-container">
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Lade Fahrzeugdaten...</div>
                ) : (
                    <table className="vehicle-table">
                        <thead>
                            <tr>
                                <th>Name / Kennzeichen</th>
                                <th>Typ</th>
                                <th>Status</th>
                                <th>TÜV</th>
                                <th>Inspektion</th>
                                <th>Versicherung</th>
                                <th>Zuständig</th>
                                <th style={{ textAlign: 'right' }}>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVehicles.map(v => {
                                const tuv = getDeadlineStatus(v.next_tuv);
                                const service = getDeadlineStatus(v.next_inspection);
                                return (
                                    <tr key={v.id} onClick={() => openDetail(v)} style={{ cursor: 'pointer' }}>
                                        <td>
                                            <div className="cell-important">{v.internal_name}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{v.license_plate}</div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {v.category_id && categories.find(c => c.id === v.category_id)?.color && (
                                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: categories.find(c => c.id === v.category_id).color }} />
                                                )}
                                                {(v.category_name && v.category_name !== 'Kein Typ') ? v.category_name : (v.vehicle_type && v.vehicle_type !== 'Kein Typ' ? v.vehicle_type : '—')}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${v.status}`}>
                                                {v.is_ready ? 'Bereit' : 'Gesperrt'}
                                            </span>
                                        </td>
                                        <td><span className={`deadline-badge ${tuv.cls}`}>{tuv.label}</span></td>
                                        <td><span className={`deadline-badge ${service.cls}`}>{service.label}</span></td>
                                        <td>
                                            {v.workshop_info?.insurance?.company ? (
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{v.workshop_info.insurance.company}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{v.workshop_info.insurance.policy_number || 'Keine Vers.-Nr.'}</div>
                                                </div>
                                            ) : (
                                                <div style={{ color: '#94a3b8' }}>—</div>
                                            )}
                                        </td>
                                        <td>{v.assigned_user_name || '—'}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: '6px', justifyItems: 'center', justifyContent: 'flex-end' }}>
                                                <button className="btn-action" style={{ padding: '4px 8px', fontSize: '0.8rem' }} onClick={(e) => handleOpenDocModal(v, e)} title="Dokumente">📂</button>
                                                <button className="btn-action" style={{ padding: '4px 8px', fontSize: '0.8rem' }} onClick={(e) => handleOpenEdit(v, e)} title="Bearbeiten">✏️</button>
                                                <button className="btn-action" style={{ padding: '4px 8px', fontSize: '0.8rem', color: '#ef4444', background: '#fef2f2', borderColor: '#fecaca' }} onClick={(e) => handleDelete(v.id, e)} title="Löschen">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredVehicles.length === 0 && (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                        Keine Fahrzeuge gefunden.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Timeline */}
            <VehicleTimeline 
                vehicles={filteredVehicles} 
                onVehicleClick={v => { setSelectedVehicle(v); setIsDetailModalOpen(true); }}
            />

            {/* Modals */}
            <VehicleModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSave={selectedVehicle ? handleUpdateVehicle : handleSaveVehicle}
                vehicle={selectedVehicle}
                companyUsers={users}
                categories={categories}
            />
            <CategoryManagerModal 
                isOpen={isCategoryModalOpen}
                onClose={() => setIsCategoryModalOpen(false)}
                categories={categories}
                onUpdate={fetchData}
            />
            <VehicleDocumentModal 
                isOpen={isDocModalOpen}
                onClose={() => setIsDocModalOpen(false)}
                vehicle={selectedVehicle}
            />

            <VehicleDetailModal 
                isOpen={isDetailModalOpen} 
                onClose={() => setIsDetailModalOpen(false)} 
                vehicle={selectedVehicle}
                categories={categories}
                companyUsers={users}
                onEdit={v => { setIsDetailModalOpen(false); handleOpenEdit(v); }}
                onDelete={(id) => { setIsDetailModalOpen(false); handleDelete(id); }}
                onDocUpload={(v) => { setIsDetailModalOpen(false); handleOpenDocModal(v); }}
            />

            {popupData && (
                <div className="modal-overlay" onClick={() => setPopupData(null)}>
                    <div className="modal-content" style={{ width: '450px', maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{ borderBottom: `2px solid ${popupData.color}` }}>
                            <div className="modal-title" style={{ color: popupData.color, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {popupData.title} <span style={{ fontSize: '0.85rem', background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '12px' }}>{popupData.vehicles.length}</span>
                            </div>
                            <button className="modal-close" onClick={() => setPopupData(null)}>✕</button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto', padding: '15px' }}>
                            {popupData.vehicles.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#64748b', margin: '20px 0' }}>Keine Fahrzeuge in dieser Kategorie.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {popupData.vehicles.map(v => (
                                        <div 
                                            key={v.id} 
                                            onClick={() => { setPopupData(null); setSelectedVehicle(v); setIsDetailModalOpen(true); }} 
                                            style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s' }}
                                            onMouseOver={e => e.currentTarget.style.borderColor = popupData.color}
                                            onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                                        >
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{v.internal_name}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{v.license_plate}</div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                <span className={`status-badge ${v.status}`} style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                                                    {v.status === 'red' ? 'Defekt' : (v.status === 'yellow' ? 'Eingeschränkt' : 'Bereit')}
                                                </span>
                                                {(v.status === 'red' && v.status_reason) && (
                                                    <span style={{ fontSize: '0.65rem', color: '#dc2626', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {v.status_reason}
                                                    </span>
                                                )}
                                                {popupData.title.includes('Dringende') && v.next_tuv && (
                                                    <span style={{ fontSize: '0.7rem', color: '#d97706', fontWeight: 600 }}>TÜV: {new Date(v.next_tuv).toLocaleDateString('de-DE')}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
